/**
 * Kiire päring (Quick Request) Floating Action Button
 * - Opens simplified 2-3 field request form from any page
 * - i18n compliant (fully Estonian when Eesti selected)
 * - Uses existing /api/service-requests endpoint
 * - Dismissible with frequency cap (shows once per session, can be permanently dismissed)
 */
import React, { useState, useEffect } from 'react';
import { useLanguage } from '@/lib/context';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Zap, X, Send, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

// i18n labels
const LABELS = {
  en: {
    buttonLabel: 'Quick Request',
    title: 'Quick Service Request',
    description: 'Describe what you need and we\'ll find providers',
    categoryLabel: 'Category',
    selectCategory: 'Select category',
    descriptionLabel: 'What do you need?',
    descriptionPlaceholder: 'Briefly describe your request...',
    emailLabel: 'Your email',
    emailPlaceholder: 'email@example.com',
    submit: 'Send Request',
    sending: 'Sending...',
    successTitle: 'Request sent!',
    successMessage: 'We\'ll notify matching providers',
    errorMessage: 'Failed to send. Try again.',
    dismiss: 'Don\'t show again',
    catTransport: 'Transport',
    catAutoRepair: 'Auto Repair',
    catRentals: 'Equipment Rental',
    catB2B: 'B2B / Subcontracting',
    catAccommodation: 'Accommodation',
    catOther: 'Other'
  },
  et: {
    buttonLabel: 'Kiire päring',
    title: 'Kiire teenusepäring',
    description: 'Kirjelda, mida vajad ja leiame sobivad pakkujad',
    categoryLabel: 'Kategooria',
    selectCategory: 'Vali kategooria',
    descriptionLabel: 'Mida vajad?',
    descriptionPlaceholder: 'Kirjelda lühidalt oma vajadust...',
    emailLabel: 'Sinu e-post',
    emailPlaceholder: 'email@näide.ee',
    submit: 'Saada päring',
    sending: 'Saatmine...',
    successTitle: 'Päring saadetud!',
    successMessage: 'Teavitame sobivaid pakkujaid',
    errorMessage: 'Saatmine ebaõnnestus. Proovi uuesti.',
    dismiss: 'Ära näita enam',
    catTransport: 'Transport',
    catAutoRepair: 'Autoremont',
    catRentals: 'Seadmete rent',
    catB2B: 'Alltöövõtt',
    catAccommodation: 'Majutus',
    catOther: 'Muu'
  },
  fi: {
    buttonLabel: 'Pikapyyntö',
    title: 'Nopea palvelupyyntö',
    description: 'Kuvaile mitä tarvitset ja etsimme tarjoajat',
    categoryLabel: 'Kategoria',
    selectCategory: 'Valitse kategoria',
    descriptionLabel: 'Mitä tarvitset?',
    descriptionPlaceholder: 'Kuvaile lyhyesti tarpeesi...',
    emailLabel: 'Sähköpostisi',
    emailPlaceholder: 'email@esimerkki.fi',
    submit: 'Lähetä pyyntö',
    sending: 'Lähetetään...',
    successTitle: 'Pyyntö lähetetty!',
    successMessage: 'Ilmoitamme sopiville tarjoajille',
    errorMessage: 'Lähetys epäonnistui. Yritä uudelleen.',
    dismiss: 'Älä näytä enää',
    catTransport: 'Kuljetus',
    catAutoRepair: 'Autokorjaus',
    catRentals: 'Laitteiden vuokraus',
    catB2B: 'Alihankinta',
    catAccommodation: 'Majoitus',
    catOther: 'Muu'
  }
};

const CATEGORIES = [
  { id: 'transport', labelKey: 'catTransport' },
  { id: 'auto_repair', labelKey: 'catAutoRepair' },
  { id: 'equipment', labelKey: 'catRentals' },
  { id: 'b2b', labelKey: 'catB2B' },
  { id: 'accommodation', labelKey: 'catAccommodation' },
  { id: 'other', labelKey: 'catOther' }
];

const STORAGE_KEY = 'keikka_quick_request_dismissed';
const SESSION_KEY = 'keikka_quick_request_shown';

export const QuickRequestFAB = () => {
  const { language } = useLanguage();
  const l = LABELS[language] || LABELS.en;
  
  const [isVisible, setIsVisible] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [formData, setFormData] = useState({
    category: '',
    description: '',
    email: ''
  });

  // Check if FAB should be shown (frequency cap)
  useEffect(() => {
    const permanentlyDismissed = localStorage.getItem(STORAGE_KEY) === 'true';
    const shownThisSession = sessionStorage.getItem(SESSION_KEY) === 'true';
    
    if (permanentlyDismissed) {
      setIsVisible(false);
      return;
    }

    // Show FAB after 3 seconds, but only once per session until interacted
    const timer = setTimeout(() => {
      if (!shownThisSession) {
        setIsVisible(true);
        sessionStorage.setItem(SESSION_KEY, 'true');
      } else {
        // If already shown this session, still show the button but don't auto-popup
        setIsVisible(true);
      }
    }, 3000);

    return () => clearTimeout(timer);
  }, []);

  const handleDismiss = () => {
    localStorage.setItem(STORAGE_KEY, 'true');
    setIsVisible(false);
    setIsOpen(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.category || !formData.description || !formData.email) {
      toast.error(language === 'et' ? 'Palun täida kõik väljad' : 'Please fill all fields');
      return;
    }

    setIsSubmitting(true);

    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${API_URL}/api/service-requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: `Quick Request: ${formData.category}`,
          description: formData.description,
          category: formData.category,
          email: formData.email,
          source: 'quick_request_fab',
          urgency: 'normal'
        })
      });

      if (response.ok) {
        setShowSuccess(true);
        setFormData({ category: '', description: '', email: '' });
        toast.success(l.successTitle);
        
        // Close after showing success
        setTimeout(() => {
          setShowSuccess(false);
          setIsOpen(false);
        }, 2000);
      } else {
        toast.error(l.errorMessage);
      }
    } catch (error) {
      console.error('Quick request error:', error);
      toast.error(l.errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isVisible) return null;

  return (
    <>
      {/* Floating Action Button - Premium neutral with orange accent */}
      <div className="fixed bottom-24 right-4 z-40 sm:bottom-8 sm:right-6">
        <Button
          onClick={() => setIsOpen(true)}
          className="h-11 px-4 bg-slate-800 hover:bg-slate-700 text-white shadow-lg rounded-full flex items-center gap-2 border border-slate-700"
          data-testid="quick-request-fab"
        >
          <Zap className="h-4 w-4 text-orange-400" />
          <span className="hidden sm:inline text-sm font-medium">{l.buttonLabel}</span>
        </Button>
      </div>

      {/* Quick Request Dialog */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Zap className="h-5 w-5 text-orange-500" />
              {l.title}
            </DialogTitle>
            <DialogDescription>
              {l.description}
            </DialogDescription>
          </DialogHeader>

          {showSuccess ? (
            <div className="py-8 text-center">
              <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-green-700">{l.successTitle}</h3>
              <p className="text-sm text-muted-foreground mt-1">{l.successMessage}</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Category Select */}
              <div>
                <label className="text-sm font-medium text-foreground mb-1.5 block">
                  {l.categoryLabel}
                </label>
                <Select 
                  value={formData.category} 
                  onValueChange={(v) => setFormData({...formData, category: v})}
                >
                  <SelectTrigger data-testid="quick-request-category">
                    <SelectValue placeholder={l.selectCategory} />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>
                        {l[cat.labelKey]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Description */}
              <div>
                <label className="text-sm font-medium text-foreground mb-1.5 block">
                  {l.descriptionLabel}
                </label>
                <Textarea
                  placeholder={l.descriptionPlaceholder}
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  rows={3}
                  className="resize-none"
                  data-testid="quick-request-description"
                />
              </div>

              {/* Email */}
              <div>
                <label className="text-sm font-medium text-foreground mb-1.5 block">
                  {l.emailLabel}
                </label>
                <Input
                  type="email"
                  placeholder={l.emailPlaceholder}
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  data-testid="quick-request-email"
                />
              </div>

              {/* Actions */}
              <div className="flex flex-col gap-2 pt-2">
                <Button 
                  type="submit" 
                  disabled={isSubmitting}
                  className="w-full bg-orange-500 hover:bg-orange-600"
                  data-testid="quick-request-submit"
                >
                  {isSubmitting ? (
                    <>
                      <span className="animate-spin mr-2">⏳</span>
                      {l.sending}
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      {l.submit}
                    </>
                  )}
                </Button>
                
                <button
                  type="button"
                  onClick={handleDismiss}
                  className="text-xs text-muted-foreground hover:text-foreground transition flex items-center justify-center gap-1"
                  data-testid="quick-request-dismiss"
                >
                  <X className="h-3 w-3" />
                  {l.dismiss}
                </button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default QuickRequestFAB;
